import { createContext, useEffect, useState } from 'react'
import { Socket, io } from 'socket.io-client'
import { useAuthContext } from '.'

/* eslint-disable react-refresh/only-export-components -- context module */

export type SocketContextType = {
    socket: Socket | null
    isConnected: boolean
    setIsConnected: (value: boolean) => void
    connect: () => void
    disconnect: () => void
}

export const SocketContext = createContext<SocketContextType>({
    socket: null,
    isConnected: false,
    setIsConnected: () => {},
    connect: () => {},
    disconnect: () => {},
})

// Runtime environment detection based on hostname
const isStaging = window.location.hostname.includes('staging')
const isProduction = window.location.hostname.includes('commzone.app') && !isStaging

// Always connect to the same origin as the page so the Vite proxy (dev) or
// nginx (staging/prod) can forward /api/socket.io to the backend.
// Hardcoding port 8000 bypasses the proxy and causes ERR_CONNECTION_REFUSED.
let baseUrl = window.location.origin
if (isStaging) {
    baseUrl = 'https://stagingapp.commzone.app'
} else if (isProduction) {
    baseUrl = 'https://app.commzone.app'
}

export const createSocket = () => {
    const socketInstance = io(baseUrl, {
        autoConnect: true,
        reconnection: true,
        withCredentials: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
        path: '/api/socket.io',
        transports: ['websocket', 'polling'], // Try both WebSocket and polling
    })

    socketInstance.on('connect_error', error => {
        console.error('Socket connection error:', error)
    })

    socketInstance.on('connect', () => {
        console.log('Socket connected successfully')
    })

    return socketInstance
}

export default function SocketContextProvider({ children }: { children: React.ReactNode }) {
    const { orgAccount, activeBusiness, user, loggedIn, isLoading } = useAuthContext()
    const [socket, setSocket] = useState<Socket | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const socketAccountId = orgAccount?.id || activeBusiness || user?.id

    const connect = () => {
        setIsConnected(true)
    }

    const disconnect = () => {
        setIsConnected(false)
    }

    useEffect(() => {
        // Cleanup previous socket if it exists
        if (socket) {
            socket.disconnect()
            socket.removeAllListeners()
        }

        // Only setup socket after auth resolves. Some restored sessions have an active
        // business before orgAccount is hydrated, but Socket.IO itself only needs a stable id.
        if (isLoading || !loggedIn || !socketAccountId) {
            if (import.meta.env.DEV) {
                console.log('Socket setup waiting for authenticated account context...')
            }
            setIsConnected(false)
            setSocket(null)
            return
        }

        if (import.meta.env.DEV) {
            console.log('Setting up socket with account id:', socketAccountId)
        }
        const socketInstance = io(baseUrl, {
            autoConnect: true,
            reconnection: true,
            withCredentials: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 10,
            path: '/api/socket.io',
            auth: {
                account_id: socketAccountId,
            },
        })

        setSocket(socketInstance)

        return () => {
            socketInstance.disconnect()
            socketInstance.removeAllListeners()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedIn, isLoading, socketAccountId]) // Re-setup socket when authenticated account context changes

    useEffect(() => {
        if (!socket) return

        socket.on('connect', () => {
            if (import.meta.env.VITE_APP_ENV !== 'production') {
                console.log('connected to web socket')
            }
            setIsConnected(true)
        })

        socket.on('disconnect', () => {
            if (import.meta.env.VITE_APP_ENV !== 'production') {
                console.log('disconnected from web socket')
            }
            setIsConnected(false)
        })

        // @ts-ignore
        socket.on('connect_error', (err: any) => {
            if (import.meta.env.VITE_APP_ENV !== 'production') {
                console.log(`connect_error due to ${err.message}`)
                console.log(`description ${JSON.stringify(err.description)}`)
                console.log(`context ${err.context}`)
            }
        })

        return () => {
            socket.off('connect')
            socket.off('disconnect')
            socket.off('connect_error')
        }
    }, [socket])

    return (
        <SocketContext.Provider value={{ socket, isConnected, setIsConnected, connect, disconnect }}>
            {children}
        </SocketContext.Provider>
    )
}
